import React, { forwardRef, useContext } from "react";
import Box from "@material-ui/core/Box";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DateRangeIcon from "@material-ui/icons/DateRange";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import DatePicker from "react-datepicker";
import { useAuth } from "./../util/auth";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { DateContext } from "../providers/DateProvider";
import { CategoryContext } from "../providers/CategoryProvider";
import styled from "styled-components";

const useStyles = makeStyles(() => ({
  root: {
    "&:not(:last-child)": {
      marginBottom: "2rem",
    },
  },
  date: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  theDay: {
    width: "400px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  theDayMobile: {
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const DatePickerWrapper = styled.section`
  .react-datepicker {
    background-color: #151824ff;
    border-radius: 5px;
    padding: 10px;
    border-color: #151824ff;
    min-height: 450px;
  }
  .react-datepicker__month-container {
    background-color: #151824ff;
    min-height: 450px;
  }
  .react-datepicker__day {
    color: white;
    line-height: 2.5rem;
  }
  .react-datepicker__day:hover {
    background-color: #0059a6;
  }
  .react-datepicker__day--keyboard-selected {
    background: none;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range {
    background-color: #e91e63;
  }
  .react-datepicker__header {
    background-color: #151824ff;
    border-bottom: 1px solid #0059a6;
  }
  .react-datepicker__current-month {
    color: white;
  }
  .react-datepicker__day-name {
    color: white;
  }
  .react-datepicker__today-button {
    background-color: #0059a6;
    color: white;
    padding: 10px;
    border-radius: 5px;
    border: none;
    position: absolute;
    bottom: 10px;
    width: 95%;
  }
  .react-datepicker__day--selected:hover {
    background-color: #e91e63;
  }
  .react-datepicker__navigation {
    top: 20px;
  }
  .react-datepicker__header__dropdown {
    padding-top: 10px;
  }
  .react-datepicker__month-select {
    color: white;
    background-color: #151824ff;
    border: none;
  }
  .react-datepicker__year-select {
    color: white;
    background-color: #151824ff;
    border: none;
  }
  .react-datepicker__day--outside-month {
    opacity: 0.2;
    pointer-events: none;
  }
`;

const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];
const weekday = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

function DateCarousel(props) {
  // ✅ dayKey is truth; selectedDate is just for UI widgets
  const { selectedDate, setSelectedDate, selectedDayKey, setSelectedDayKey, dayLabel } =
    useContext(DateContext);

  const { category, setCategory } = useContext(CategoryContext);

  const classes = useStyles();
  const theme = useTheme();
  const ifSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const auth = useAuth();

  const bumpDay = (deltaDays) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + deltaDays);
    setSelectedDate(d); // provider converts to dayKey and normalizes

    if (category === "production") {
      setCategory("shooting");
    }
  };

  const prevDay = () => bumpDay(-1);
  const nextDay = () => bumpDay(1);

  function readableDay(separator = ", ") {
    // You can also just use dayLabel, but keeping your formatting as-is:
    const d = new Date(selectedDate);
    const dateNum = d.getDate();
    const theDay = weekday[d.getDay()];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${theDay}, ${month} ${dateNum}${separator}${year}`;
  }

  function mobileDay() {
    const d = new Date(selectedDate);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${yyyy}`;
  }

  const DateTitlePickerInput = forwardRef(({ onClick }, ref) => (
    <div
      className={ifSmall ? classes.theDayMobile : classes.theDay}
      onClick={onClick}
      ref={ref}
    >
      {ifSmall ? mobileDay() : readableDay()}{" "}
      <DateRangeIcon style={{ marginLeft: 5 }} color="secondary" />
    </div>
  ));

  return (
    <Box className={classes.root + (props.className ? ` ${props.className}` : "")}>
      <Typography variant="h5">
        {auth.user.planId && (
          <div className={classes.date}>
            <Button onClick={prevDay}>
              <ChevronLeftIcon fontSize="large" color="secondary" />
            </Button>

            <DatePickerWrapper>
              <DatePicker
                selected={selectedDate} // ✅ Date for the picker UI
                onChange={(date) => {
                  if (!date) return;
                  setSelectedDate(date); // ✅ updates selectedDayKey under the hood
                  if (category === "production") {
                    setCategory("shooting");
                  }
                }}
                customInput={<DateTitlePickerInput />}
                todayButton="Today"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                withPortal
              />
            </DatePickerWrapper>

            <Button onClick={nextDay}>
              <ChevronRightIcon fontSize="large" color="secondary" />
            </Button>
          </div>
        )}
      </Typography>
    </Box>
  );
}

export default DateCarousel;